import { createApi } from '@reduxjs/toolkit/query/react';

import { createBaseQueryWithReauth } from '@/services';
import { API_BUSINESS_URL } from '@/utils';
import { PaginatedRequest, PaginatedResponse } from '@/types';

const isUuid = (value?: string | null): boolean =>
  typeof value === 'string'
    ? /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
    : false;

interface GeocodeAddressRequest {
  addressId: string;
  lat?: string;
  long?: string;
  cityId?: string;
  stateOrDepartmentId?: string;
  logisticOperatorId?: string;
  updateZone?: boolean;
}

interface GeocodeZoneMatch {
  zoneId: string;
  zoneTypeId: string;
  zoneName: string;
  zoneTypeName: string;
  isInsidePolygon: boolean;
  distanceInMeters: number;
}

interface GeocodeAddressResponse {
  addressId: string;
  lat?: string;
  long?: string;
  zipCode?: string;
  neighborhood?: string;
  message?: string;
  zoneId?: string | null;
  zoneName?: string | null;
  zoneTypeId?: string | null;
  zoneTypeName?: string | null;
  zones?: GeocodeZoneMatch[];
  zoneError?: boolean;
  zoneErrorDescription?: string | null;
}

interface GeocodeByAddressRequest {
  streetName: string;
  doorNumber: string;
  apartmentNumber?: string;
  corner?: string;
  zipCode?: string;
  cityId?: string;
  cityName?: string;
  stateOrDepartmentId?: string;
  stateOrDepartmentName?: string;
  country?: string;
  includeZone?: boolean;
  logisticOperatorId?: string;
}

interface GeocodeByAddressZone {
  zoneId?: string | null;
  zoneName?: string | null;
  zoneTypeId?: string | null;
  zoneTypeName?: string | null;
  zones?: Array<{
    zoneId: string;
    zoneTypeId: string;
    zoneName: string;
    zoneTypeName: string;
    isInsidePolygon: boolean;
    distanceInMeters: number;
  }>;
}

interface GeocodeByAddressData {
  lat?: string;
  long?: string;
  zipCode?: string | null;
  neighborhood?: string | null;
  geocodeMethod?: string | null;
  zone?: GeocodeByAddressZone;
}

interface GeocodeByAddressResponse {
  message?: string;
  data?: GeocodeByAddressData;
}

interface ReverseGeocodeRequest {
  latitude: number;
  longitude: number;
}

interface ReverseGeocodeResponse {
  latitude: number;
  longitude: number;
  street?: string;
  number?: string;
  city?: string;
  region?: string;
  country?: string;
  provider?: string;
  neighborhoodId?: string;
  neighborhoodName?: string;
  zipCode?: string;
  message?: string;
}

interface DepartmentApi {
  id: string;
  name: string;
  code?: string;
  type?: string;
  lat?: number;
  lng?: number;
  country?: string;
}

interface CityApi {
  id: string;
  name: string;
  departmentId: string;
  stateOrDepartmentId?: string;
}

interface NeighborhoodApi {
  id: string;
  name: string;
  cityId: string;
  externalId?: string;
  city?: string;
  country?: string;
  type?: string;
  lat?: number;
  lng?: number;
}

const geocodingService = createApi({
  reducerPath: 'geocodingService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}`),
  tagTypes: ['Geocoding', 'Departments', 'Cities', 'Neighborhoods'],
  endpoints: (builder) => ({
    geocodeAddress: builder.mutation<GeocodeAddressResponse, GeocodeAddressRequest>({
      query: (body) => ({
        url: `/geocoding/geocode`,
        method: 'POST',
        body: {
          addressId: body.addressId,
          ...(body.updateZone && { updateZone: true }),
          ...(body.updateZone &&
            body.logisticOperatorId && {
              logisticOperatorId: body.logisticOperatorId,
            }),
        },
        ...(body.updateZone &&
          body.logisticOperatorId && {
            headers: {
              'x-logistic-operator-id': body.logisticOperatorId,
            },
          }),
      }),
      transformErrorResponse: (response: unknown) => {
        if (response && typeof response === 'object') {
          const data = response as Record<string, unknown>;
          if (data.lat || data.long) {
            return {
              data: {
                addressId: (data.addressId as string) || '',
                lat: data.lat as string | undefined,
                long: data.long as string | undefined,
                message: data.message as string | undefined,
              },
            };
          }
        }
        return response;
      },
    }),
    geocodeByAddress: builder.mutation<GeocodeByAddressResponse, GeocodeByAddressRequest>({
      query: ({ logisticOperatorId, ...body }) => ({
        url: '/geocoding/geocode-by-address',
        method: 'POST',
        body,
        ...(logisticOperatorId && {
          headers: { 'x-logistic-operator-id': logisticOperatorId },
        }),
      }),
    }),
    reverseGeocode: builder.mutation<ReverseGeocodeResponse, ReverseGeocodeRequest>({
      query: (body) => ({
        url: '/geocoding/reverse-geocode',
        method: 'POST',
        body: {
          latitude: body.latitude,
          longitude: body.longitude,
        },
      }),
    }),
    getDepartments: builder.query<
      PaginatedResponse<DepartmentApi>,
      {
        paginated: PaginatedRequest;
        country?: string;
        countryId?: string;
        search?: string;
      }
    >({
      query: ({ paginated, country, countryId, search }) => {
        if (!country) {
          return {
            url: '/states-or-departments',
            params: {
              limit: paginated.pageSize,
              offset: (paginated.page - 1) * paginated.pageSize,
              ...(search && { name: search }),
              ...(countryId && { countryId }),
            },
          };
        }
        return {
          url: `/geographic/departments/${encodeURIComponent(country)}`,
          params: {
            limit: paginated.pageSize,
            offset: (paginated.page - 1) * paginated.pageSize,
            ...(search && { search }),
          },
        };
      },
      transformResponse: (response: { departments?: DepartmentApi[]; count?: number; items?: DepartmentApi[] }) => {
        if (response.departments) {
          return {
            items: response.departments,
            count: response.count || response.departments.length,
          };
        }
        if (response.items) {
          return {
            items: response.items,
            count: response.count || response.items.length,
          };
        }
        return {
          items: [],
          count: 0,
        };
      },
      providesTags: ['Departments'],
    }),
    getCities: builder.query<
      PaginatedResponse<CityApi>,
      {
        paginated: PaginatedRequest;
        department?: string;
        stateOrDepartmentId?: string;
        search?: string;
        searchParamKey?: 'search' | 'name';
      }
    >({
      query: ({
        paginated,
        department,
        stateOrDepartmentId,
        search,
        searchParamKey = 'search',
      }) => {
        const searchParam = search ? { [searchParamKey]: search } : {};
        if (stateOrDepartmentId) {
          return {
            url: '/cities',
            params: {
              limit: paginated.pageSize,
              offset: (paginated.page - 1) * paginated.pageSize,
              stateOrDepartmentId,
              ...searchParam,
            },
          };
        }
        if (!department) {
          return {
            url: '/cities',
            params: {
              limit: paginated.pageSize,
              offset: (paginated.page - 1) * paginated.pageSize,
              ...searchParam,
            },
          };
        }
        return {
          url: `/geographic/cities/${encodeURIComponent(department)}`,
          params: {
            limit: paginated.pageSize,
            offset: (paginated.page - 1) * paginated.pageSize,
            ...searchParam,
          },
        };
      },
      transformResponse: (response: { cities?: CityApi[]; count?: number; items?: CityApi[] }) => {
        if (response.cities) {
          return {
            items: response.cities,
            count: response.count || response.cities.length,
          };
        }
        if (response.items) {
          return {
            items: response.items,
            count: response.count || response.items.length,
          };
        }
        return {
          items: [],
          count: 0,
        };
      },
      providesTags: ['Cities'],
    }),
    getCitiesFromGeomancer: builder.query<
      PaginatedResponse<CityApi>,
      {
        paginated: PaginatedRequest;
        department: string;
        stateOrDepartmentId: string;
        search?: string;
        country?: string;
      }
    >({
      query: ({ paginated, department, stateOrDepartmentId, search, country }) => ({
        url: '/cities/from-geomancer',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          department,
          stateOrDepartmentId,
          ...(country ? { country } : {}),
          ...(search ? { search } : {}),
        },
      }),
      transformResponse: (response: { count?: number; items?: CityApi[] }) => ({
        items: response.items ?? [],
        count: response.count ?? response.items?.length ?? 0,
      }),
      providesTags: ['Cities'],
    }),
    getNeighborhoods: builder.query<
      PaginatedResponse<NeighborhoodApi>,
      { paginated: PaginatedRequest; cityId?: string; cityName?: string; search?: string }
    >({
      query: ({ paginated, cityId, cityName, search }) => {
        if (cityName) {
          return {
            url: `/geographic/neighborhoods/${encodeURIComponent(cityName)}`,
            params: {
              limit: paginated.pageSize,
              offset: (paginated.page - 1) * paginated.pageSize,
              ...(search && { search }),
            },
          };
        }
        
        if (!cityId) {
          return {
            url: '/neighborhoods',
            params: {
              limit: paginated.pageSize,
              offset: (paginated.page - 1) * paginated.pageSize,
              ...(search && { search }),
            },
          };
        }
        
        if (isUuid(cityId)) {
          return {
            url: `/neighborhoods/by-city/${cityId}`,
            params: {
              limit: paginated.pageSize,
              offset: (paginated.page - 1) * paginated.pageSize,
              ...(search && { search }),
            },
          };
        }
        
        return {
          url: '/neighborhoods',
          params: {
            limit: paginated.pageSize,
            offset: (paginated.page - 1) * paginated.pageSize,
            ...(search && { search }),
          },
        };
      },
      transformResponse: (response: { neighborhoods?: NeighborhoodApi[]; count?: number; items?: NeighborhoodApi[] }) => {
        if (response.neighborhoods) {
          return {
            items: response.neighborhoods,
            count: response.count || response.neighborhoods.length,
          };
        }
        if (response.items) {
          return {
            items: response.items,
            count: response.count || response.items.length,
          };
        }
        return {
          items: [],
          count: 0,
        };
      },
      providesTags: ['Neighborhoods'],
    }),
    createDepartment: builder.mutation<
      DepartmentApi,
      { name: string; countryId?: string; code?: string }
    >({
      query: (body) => {
        const code =
          body.code ||
          body.name
            .substring(0, 10)
            .toUpperCase()
            .replace(/[^A-Z0-9]/g, '')
            .padEnd(2, 'X')
            .substring(0, 10);
        return {
          url: '/states-or-departments',
          method: 'POST',
          body: {
            name: body.name,
            code,
            ...(body.countryId && { countryId: body.countryId }),
          },
        };
      },
      invalidatesTags: ['Departments'],
    }),
    createCity: builder.mutation<CityApi, { name: string; stateOrDepartmentId: string; code?: string }>({
      query: (body) => {
        const code = body.code || body.name.substring(0, 10).toUpperCase().replace(/[^A-Z0-9]/g, '').padEnd(2, 'X').substring(0, 10);
        return {
          url: '/cities',
          method: 'POST',
          body: {
            name: body.name,
            stateOrDepartmentId: body.stateOrDepartmentId,
            code: code,
          },
        };
      },
      invalidatesTags: ['Cities'],
    }),
    createNeighborhood: builder.mutation<NeighborhoodApi, { name: string; cityId: string; externalId?: string; code?: string }>({
      query: (body) => {
        const code = body.code || body.name.substring(0, 10).toUpperCase().replace(/[^A-Z0-9]/g, '').padEnd(2, 'X').substring(0, 10);
        return {
          url: '/neighborhoods',
          method: 'POST',
          body: {
            name: body.name,
            cityId: body.cityId,
            code: code,
            ...(body.externalId && { externalId: body.externalId }),
          },
        };
      },
      invalidatesTags: ['Neighborhoods'],
    }),
  }),
});

export default geocodingService;

export const {
  useGeocodeAddressMutation,
  useGeocodeByAddressMutation,
  useReverseGeocodeMutation,
  useGetDepartmentsQuery,
  useLazyGetDepartmentsQuery,
  useGetCitiesQuery,
  useLazyGetCitiesQuery,
  useGetCitiesFromGeomancerQuery,
  useLazyGetCitiesFromGeomancerQuery,
  useGetNeighborhoodsQuery,
  useCreateDepartmentMutation,
  useCreateCityMutation,
  useCreateNeighborhoodMutation,
} = geocodingService;

export type { GeocodeByAddressRequest, GeocodeByAddressResponse, ReverseGeocodeRequest, ReverseGeocodeResponse, DepartmentApi, CityApi, NeighborhoodApi };
